/**
 * Public pricing page — Patch 25 Part I + III + VII.
 *
 * Fetches the pricing catalog from /api/v1/pricing/catalog. Renders the
 * six plans with the Growth tier highlighted, and the six add-on
 * categories below. Positioning language ("Marketing Department") is
 * pulled from the API so a single edit updates every surface.
 */
import { useQuery } from "@tanstack/react-query";
import { Check, X, Sparkles } from "lucide-react";

import { http } from "@/shared/api/http";


interface CatalogPlan {
  key: string;
  display_name: string;
  price_usd: number | null;
  price_suffix: string;
  recommended: boolean;
  hidden: boolean;
  contracted: boolean;
  tagline: string;
  limits: {
    workspaces: number | null;
    text_generations: number | null;
    premium_images_per_month: number | null;
    premium_videos_per_month: number | null;
    video_seconds_per_month: number | null;
    voice_minutes_per_month: number | null;
    team_seats: number | null;
    storage_gb: number | null;
    autopilot_max_level: "advisor" | "assistant" | "operator" | "executive";
  };
  capabilities: string[];
}

interface CatalogAddOn {
  key: string;
  kind: string;
  amount: number;
  price_usd: number;
  recurring: boolean;
  display_name: string;
}

interface Catalog {
  plans: CatalogPlan[];
  add_ons: CatalogAddOn[];
  language: { positioning: string };
}


async function fetchCatalog(): Promise<Catalog> {
  return await http<Catalog>("/pricing/catalog");
}


function unl(n: number | null): string {
  return n === null ? "Unlimited" : n.toLocaleString();
}


const CAPABILITY_ROWS = [
  { cap: "brand_brain",         label: "Brand Brain" },
  { cap: "business_brain",      label: "Business Brain" },
  { cap: "master_prompt_engine", label: "Master Prompt Engine" },
  { cap: "content_calendar",    label: "Content Calendar" },
  { cap: "content_vault",       label: "Content Vault" },
  { cap: "publishing",          label: "Publishing" },
  { cap: "approval_queue",      label: "Approval Queue" },
  { cap: "email_campaigns",     label: "Email Campaigns" },
  { cap: "autopilot",           label: "Autopilot" },
  { cap: "analytics",           label: "Analytics" },
  { cap: "creative_director",   label: "Creative Director" },
  { cap: "team_collaboration",  label: "Team Collaboration" },
];


export default function PricingPage(): JSX.Element {
  const q = useQuery({ queryKey: ["pricing-catalog"], queryFn: fetchCatalog });
  const plans = q.data?.plans ?? [];
  const addons = q.data?.add_ons ?? [];

  return (
    <div data-testid="pricing-page" className="mx-auto max-w-[1200px] px-6 py-16">
      <div className="text-center">
        <div className="md-eyebrow md-eyebrow--amber inline-flex items-center gap-2">
          <Sparkles className="h-3.5 w-3.5" /> Hire your Marketing Department
        </div>
        <h1 className="mt-3 font-display italic text-4xl md:text-5xl">
          Pricing that grows with you.
        </h1>
        <p className="mx-auto mt-3 max-w-xl text-sm text-[var(--md-fog)]">
          {q.data?.language.positioning ??
            "You aren't buying credits. You're hiring your Marketing Department."}
        </p>
      </div>

      {q.isLoading && (
        <p className="mt-16 text-center text-sm text-[var(--md-fog)]">Loading catalog…</p>
      )}

      {/* Plan cards */}
      <div className="mt-14 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {plans.map((p) => (
          <div
            key={p.key}
            data-testid={`plan-card-${p.key}`}
            className={`relative rounded-2xl border p-6 ${
              p.recommended
                ? "border-[var(--md-amber)] bg-[var(--md-ink-1)]"
                : "border-[color:var(--md-line)] bg-[var(--md-ink-1)]/60"
            }`}
          >
            {p.recommended && (
              <span className="absolute -top-3 left-6 rounded-full bg-[var(--md-amber)] px-3 py-0.5 text-xs font-semibold text-black">
                Recommended
              </span>
            )}
            <h3 className="text-xl font-semibold">{p.display_name}</h3>
            <p className="mt-1 min-h-[36px] text-xs text-[var(--md-fog)]">{p.tagline}</p>
            <div className="mt-4">
              <span className="font-display text-4xl">
                {p.price_usd === null ? "Custom" : `$${p.price_usd}`}
              </span>
              <span className="ml-1 text-xs text-[var(--md-fog)]">{p.price_suffix}</span>
            </div>
            <ul className="mt-5 space-y-2 text-sm">
              <li data-testid={`plan-${p.key}-workspaces`}>
                <b>{p.contracted && p.limits.workspaces === null ? "Contracted" : unl(p.limits.workspaces)}</b>{" "}
                workspace{p.limits.workspaces === 1 ? "" : "s"}
              </li>
              <li data-testid={`plan-${p.key}-text-generations`}>
                <b>{unl(p.limits.text_generations)}</b> text generations / mo
              </li>
              <li data-testid={`plan-${p.key}-images`}>
                <b>{unl(p.limits.premium_images_per_month)}</b> premium images / mo
              </li>
              <li data-testid={`plan-${p.key}-videos`}>
                <b>{unl(p.limits.video_seconds_per_month)}</b> included video seconds / mo
              </li>
              <li data-testid={`plan-${p.key}-voice`}>
                <b>{unl(p.limits.voice_minutes_per_month)}</b> voice minutes / mo
              </li>
              <li data-testid={`plan-${p.key}-seats`}>
                <b>{p.contracted && p.limits.team_seats === null ? "Contracted" : unl(p.limits.team_seats)}</b>{" "}
                team member{p.limits.team_seats === 1 ? "" : "s"}
              </li>
              <li>
                <b>{unl(p.limits.storage_gb)} GB</b> storage
              </li>
            {p.capabilities.includes("autopilot") && (
              <li>
                <b>{p.limits.autopilot_max_level === "executive" ? "All trust levels" : "Advisor"}</b> Autopilot
              </li>
            )}
            </ul>
            <details className="mt-5 text-xs">
              <summary className="cursor-pointer text-[var(--md-fog)]">See all capabilities</summary>
              <ul className="mt-3 space-y-1">
                {CAPABILITY_ROWS.map((r) => {
                  const has = p.capabilities.includes(r.cap);
                  return (
                    <li key={r.cap} className="flex items-center gap-2">
                      {has ? (
                        <Check className="h-3.5 w-3.5 text-emerald-500" />
                      ) : (
                        <X className="h-3.5 w-3.5 text-[var(--md-fog)] opacity-50" />
                      )}
                      <span className={has ? "text-[var(--md-ivory-0)]" : "text-[var(--md-fog)] line-through"}>
                        {r.label}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </details>
            <button
              type="button"
              disabled
              data-testid={`plan-${p.key}-cta`}
              aria-label={`${p.display_name} enrollment is coming soon`}
              className={`mt-6 block w-full text-center ${
                p.recommended ? "md-btn md-btn--primary" : "md-btn md-btn--ghost"
              }`}
              style={{ padding: "12px 22px", fontSize: 14 }}
            >
              {p.key === "enterprise_v2" ? "Contact sales — coming soon" : "Enrollment coming soon"}
            </button>
          </div>
        ))}
      </div>

      {/* Add-on packs */}
      <div className="mt-16">
        <h2 className="text-center font-display italic text-3xl">Add-On Packs</h2>
        <p className="mt-1 text-center text-xs text-[var(--md-fog)]">
          One-time generation packs apply to the current billing period. Recurring packs renew monthly until canceled.
        </p>
        <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {Object.entries(
            addons.reduce<Record<string, CatalogAddOn[]>>((acc, a) => {
              (acc[a.kind] ??= []).push(a);
              return acc;
            }, {}),
          ).map(([kind, packs]) => (
            <div
              key={kind}
              data-testid={`addon-group-${kind}`}
              className="rounded-2xl border border-[color:var(--md-line)] bg-[var(--md-ink-1)]/60 p-5"
            >
              <h3 className="text-sm font-semibold uppercase tracking-widest text-[var(--md-amber)]">
                {kind.replace(/_/g, " ")}
              </h3>
              <ul className="mt-3 space-y-2 text-sm">
                {packs.map((a) => (
                  <li key={a.key} className="flex items-center justify-between">
                    <span>{a.display_name}</span>
                    <b>
                      ${a.price_usd}
                      {a.recurring && <span className="text-xs text-[var(--md-fog)]">/mo</span>}
                    </b>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-16 text-center text-xs text-[var(--md-fog)]">
        Orkexra chooses the best provider automatically. You never buy providers or tokens.
      </div>
    </div>
  );
}
